import { useAdminStore } from '@/stores/adminStore';
import { Database, Server, MapPin, HardDrive } from 'lucide-react';

interface HealthCardProps {
  label: string;
  icon: typeof Database;
  connected: boolean;
  detail?: string;
  error?: string;
}

function HealthCard({ label, icon: Icon, connected, detail, error }: HealthCardProps) {
  return (
    <div className={`p-4 rounded-xl border ${connected ? 'bg-bg-tertiary/50 border-border-subtle' : 'bg-accent-red/5 border-accent-red/20'}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${connected ? 'bg-accent-green/15 text-accent-green' : 'bg-accent-red/15 text-accent-red'}`}>
          <Icon size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-text-primary">{label}</div>
        </div>
        <div className={`w-3 h-3 rounded-full ${connected ? 'bg-accent-green shadow-[0_0_8px_var(--accent-green)]' : 'bg-accent-red shadow-[0_0_8px_var(--accent-red)]'}`} />
      </div>
      {detail && (
        <div className="text-[11px] text-text-muted mt-1">{detail}</div>
      )}
      {error && (
        <div className="text-[11px] text-accent-red mt-1 truncate">{error}</div>
      )}
    </div>
  );
}

export default function SystemHealthTab() {
  const health = useAdminStore((s) => s.systemHealth);

  if (!health) {
    return (
      <div className="text-center text-text-muted text-sm py-12">
        Cargando estado del sistema...
      </div>
    );
  }

  const redis = health.redis || { connected: false };
  const pg = health.postgresql || { connected: false };
  const osrm = health.osrm || { connected: false };
  const data = health.data || {};

  const dataConnected = !data.is_stale && !data.error;
  const dataDetail = data.record_count
    ? `${data.record_count.toLocaleString()} registros${data.last_sync ? ` | Sync: ${new Date(data.last_sync).toLocaleTimeString()}` : ''}`
    : data.last_sync
    ? `Sync: ${new Date(data.last_sync).toLocaleTimeString()}`
    : undefined;

  return (
    <div className="grid grid-cols-2 gap-3">
      <HealthCard
        label="Redis"
        icon={Database}
        connected={redis.connected}
        detail={redis.last_flush ? `Flush: ${new Date(redis.last_flush).toLocaleTimeString()}` : undefined}
        error={redis.error}
      />
      <HealthCard
        label="PostgreSQL"
        icon={Server}
        connected={pg.connected}
        error={pg.error}
      />
      <HealthCard
        label="OSRM"
        icon={MapPin}
        connected={osrm.connected}
        error={osrm.error}
      />
      <HealthCard
        label="Datos"
        icon={HardDrive}
        connected={dataConnected}
        detail={dataDetail}
        error={data.error}
      />
    </div>
  );
}
